import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, list } from '../lib/api'
import { Empty, Money, PageHead, Severity, Skel, Stat } from '../components/ui'
import { money, number, thaiDateTime } from '../lib/format'

/**
 * Enterprise transaction simulation and stress testing (part 66).
 *
 * The browser does not generate or score anything. It sends a seed, a row count and a fraud rate;
 * the server generates the ledger, runs the same deterministic engines used on real transactions,
 * and stores the run. This page is a window onto stored evidence: every number below was recorded
 * when the run executed, and the totals hash proves that re-running the same seed reproduces it.
 *
 * The p95 shown here is the per-batch scoring latency, not a per-row average. A simulation is
 * scored in batches of up to 100 rows, so the percentile has a real unit of work behind it; an
 * average over 1,000 tiny loops would hide the tail that matters in a stress test.
 */

interface SimRun {
  id: string
  seed: number
  requested_count: number
  transaction_count: number
  fraud_rate_bp: number
  fraud_injected: number
  fraud_detected: number
  legit_count: number
  false_positive_count: number
  flagged_count: number
  finding_count: number
  detection_rate_bp: number
  precision_bp: number
  total_amount_satang: number
  debit_amount_satang: number
  credit_amount_satang: number
  batch_count: number
  batch_size: number
  generation_micros: number
  scoring_micros: number
  total_micros: number
  throughput_per_sec: number
  p50_micros: number
  p95_micros: number
  p99_micros: number
  max_micros: number
  totals_hash: string
  window_start: string
  window_end: string
  generated_by?: string
  generated_at: string
}

interface SimResult {
  rule_code: string
  title_th: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  hit_count: number
  transaction_count: number
  injected_transaction_count: number
  amount_at_risk_satang: number
}

interface SimRunView {
  run: SimRun
  results: SimResult[]
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function latency(micros: number): string {
  if (micros < 1000) return `${number(micros)} µs`
  return `${(micros / 1000).toFixed(2)} ms`
}

function ratePercent(bp: number): string {
  return `${(bp / 100).toFixed(2)}%`
}

function shortHash(hash: string): string {
  return hash.length <= 16 ? hash : `${hash.slice(0, 12)}…${hash.slice(-4)}`
}

function RunRow({ run, active, onSelect }: { run: SimRun; active: boolean; onSelect: () => void }) {
  const tone = run.fraud_detected === run.fraud_injected ? 'sev-low' : 'sev-high'
  return (
    <tr onClick={onSelect} style={{ cursor: 'pointer', background: active ? 'rgba(200, 169, 106, 0.06)' : undefined }}>
      <td className="tnum">{thaiDateTime(run.generated_at)}</td>
      <td className="mono">{run.seed}</td>
      <td className="num tnum">{number(run.transaction_count)}</td>
      <td className="num tnum">{ratePercent(run.fraud_rate_bp)}</td>
      <td className={`num tnum ${tone}`}>
        {number(run.fraud_detected)}/{number(run.fraud_injected)}
      </td>
      <td className="num tnum">{latency(run.p95_micros)}</td>
      <td>{active ? 'กำลังดู' : 'เปิดผล'}</td>
    </tr>
  )
}

export function Simulation() {
  const qc = useQueryClient()
  const [seed, setSeed] = useState('20260105')
  const [count, setCount] = useState('1000')
  const [fraudPercent, setFraudPercent] = useState('5.00')
  const [selectedID, setSelectedID] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const countNum = Number(count)
  const fraudBP = Math.round(Number(fraudPercent) * 100)
  const valid =
    Number.isInteger(Number(seed)) &&
    Number.isInteger(countNum) && countNum >= 1 && countNum <= 10_000 &&
    Number.isFinite(Number(fraudPercent)) && fraudBP >= 0 && fraudBP <= 10_000

  const runs = useQuery({
    queryKey: ['simulation-runs'],
    queryFn: () => list<SimRun>('/v1/simulation/runs?limit=50'),
    staleTime: 30_000,
  })

  const active = runs.data?.find((r) => r.id === selectedID) ?? runs.data?.[0]

  const detail = useQuery({
    queryKey: ['simulation-run', active?.id],
    queryFn: () => api<SimRunView>(`/v1/simulation/runs/${active?.id}/results`),
    enabled: Boolean(active?.id),
    staleTime: 60_000,
  })

  const start = useMutation({
    mutationFn: () =>
      apiWrite<SimRunView>('/v1/simulation/runs', 'POST', {
        seed: Number(seed),
        transaction_count: countNum,
        fraud_rate_bp: fraudBP,
      }),
    onSuccess: (view) => {
      setNotice(
        `สร้างการจำลองแล้ว · ตรวจพบ ${number(view.run.fraud_detected)} จาก ${number(view.run.fraud_injected)} รายการที่ฝัง · p95 ${latency(view.run.p95_micros)}`,
      )
      qc.setQueryData(['simulation-run', view.run.id], view)
      qc.setQueryData<SimRun[]>(['simulation-runs'], (old) => [
        view.run,
        ...(old ?? []).filter((r) => r.id !== view.run.id),
      ])
      setSelectedID(view.run.id)
      void qc.invalidateQueries({ queryKey: ['simulation-runs'] })
    },
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!valid || start.isPending) return
    setNotice(null)
    start.mutate()
  }

  const run = detail.data?.run
  const results = detail.data?.results ?? []
  const firing = results.filter((r) => r.hit_count > 0)
  const busy = start.isPending

  return (
    <>
      <PageHead
        title="จำลองธุรกรรมและทดสอบภาระ"
        sub="สร้างบัญชีแยกประเภทสังเคราะห์จาก seed ที่กำหนด ฝังรายการทุจริตตามอัตราที่เลือก แล้วให้กฎ 13 ข้อชุดเดียวกับ ledger จริงเป็นผู้ตัดสิน · ผลลัพธ์ถูกเก็บแบบแก้ไขไม่ได้"
        actions={
          <div className="row" style={{ gap: 8 }}>
            <span className="chip gold">ผลลัพธ์อ้างอิงจาก seed</span>
            <span className="chip">ไม่แตะตารางจริง</span>
          </div>
        }
      />

      {notice ? (
        <div
          role="status"
          style={{
            marginTop: 4,
            marginBottom: 16,
            padding: '10px 14px',
            fontSize: 12.5,
            border: '1px solid var(--color-emerald-soft)',
            borderRadius: 6,
            color: 'var(--color-emerald-soft)',
          }}
        >
          {notice}
        </div>
      ) : null}

      {start.isError ? (
        <div className="form-error" style={{ marginBottom: 16 }}>
          {errorText(start.error, 'เริ่มการจำลองไม่สำเร็จ')}
        </div>
      ) : null}

      <div className="grid-main">
        <form className="card card-pad" onSubmit={submit}>
          <div className="card-label">ตั้งค่าการจำลอง</div>
          <p className="dim" style={{ fontSize: 12, lineHeight: 1.75, marginTop: 10 }}>
            ค่าเดียวกันต้องได้ผลเดียวกันทุกครั้ง — seed ถูกบันทึกพร้อมผลรวมและค่าแฮช เพื่อให้ตรวจซ้ำได้แม้เวลาผ่านไป
          </p>
          <div className="grid-3" style={{ marginTop: 14 }}>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="sim-seed">Seed</label>
              <input
                id="sim-seed"
                className="input mono"
                type="number"
                step="1"
                value={seed}
                onChange={(e) => setSeed(e.target.value)}
                disabled={busy}
              />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="sim-count">จำนวนธุรกรรม</label>
              <input
                id="sim-count"
                className="input"
                type="number"
                min={1}
                max={10_000}
                step="1"
                value={count}
                onChange={(e) => setCount(e.target.value)}
                disabled={busy}
              />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="sim-fraud">อัตราทุจริตที่ฝัง (%)</label>
              <input
                id="sim-fraud"
                className="input"
                type="number"
                min={0}
                max={100}
                step="0.01"
                value={fraudPercent}
                onChange={(e) => setFraudPercent(e.target.value)}
                disabled={busy}
              />
            </div>
          </div>
          <div className="row" style={{ marginTop: 16 }}>
            <button className="btn btn-primary" type="submit" disabled={!valid || busy}>
              {busy ? 'กำลังสร้างและให้คะแนน…' : 'เริ่มการจำลอง'}
            </button>
            <span className="dim" style={{ fontSize: 11.5 }}>
              {valid
                ? `จะสร้าง ${number(countNum)} รายการ · ฝังประมาณ ${number(Math.round((countNum * fraudBP) / 10_000))} รายการ · ${ratePercent(fraudBP)}`
                : 'จำนวนต้องอยู่ระหว่าง 1 ถึง 10,000 และอัตราต้องอยู่ระหว่าง 0 ถึง 100%'}
            </span>
          </div>
          <p className="dim" style={{ fontSize: 11.5, lineHeight: 1.8, marginTop: 14, marginBottom: 0 }}>
            ระบบแบ่งการให้คะแนนเป็นชุดละ 100 รายการ เพื่อวัด p95 ของงานจริงหนึ่งหน่วย ไม่ใช่ค่าเฉลี่ยของลูปเล็ก ๆ
            ที่ซ่อนหางความหน่วงไว้
          </p>
        </form>

        <div className="card card-pad">
          <div className="row" style={{ alignItems: 'baseline' }}>
            <span className="card-label">การจำลองล่าสุด</span>
            <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>{number(runs.data?.length ?? 0)} รายการ</span>
          </div>
          {runs.isLoading ? (
            <div className="stack" style={{ marginTop: 12 }}><Skel h={40} /><Skel h={40} /><Skel h={40} /></div>
          ) : runs.isError ? (
            <div className="form-error" style={{ marginTop: 12 }}>{errorText(runs.error, 'โหลดรายการจำลองไม่สำเร็จ')}</div>
          ) : (runs.data ?? []).length === 0 ? (
            <div style={{ marginTop: 12 }}>
              <Empty title="ยังไม่มีการจำลอง" body="ตั้ง seed จำนวนธุรกรรม และอัตราทุจริตแล้วกดเริ่ม ระบบจะเก็บผลไว้ที่นี่" />
            </div>
          ) : (
            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table className="table data">
                <thead>
                  <tr>
                    <th>เวลา</th>
                    <th>Seed</th>
                    <th style={{ textAlign: 'right' }}>รายการ</th>
                    <th style={{ textAlign: 'right' }}>อัตรา</th>
                    <th style={{ textAlign: 'right' }}>ตรวจพบ/ฝัง</th>
                    <th style={{ textAlign: 'right' }}>p95</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(runs.data ?? []).map((r) => (
                    <RunRow
                      key={r.id}
                      run={r}
                      active={r.id === active?.id}
                      onSelect={() => setSelectedID(r.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {detail.isLoading ? (
        <div className="stack" style={{ marginTop: 18 }}><Skel h={110} /><Skel h={280} /></div>
      ) : detail.isError ? (
        <div className="card card-pad" style={{ marginTop: 18 }}>
          <div className="form-error">{errorText(detail.error, 'โหลดผลการจำลองไม่สำเร็จ')}</div>
        </div>
      ) : !run ? (
        <div className="card card-pad" style={{ marginTop: 18 }}>
          <Empty title="ยังไม่มีผลให้แสดง" body="เริ่มการจำลองหนึ่งครั้ง หรือเลือกจากรายการล่าสุดด้านบน" />
        </div>
      ) : (
        <>
          <div className="grid-4 stagger" style={{ marginTop: 18 }}>
            <Stat
              label="ธุรกรรมที่สร้าง"
              value={number(run.transaction_count)}
              unit="รายการ"
              foot={`${number(run.batch_count)} ชุด × ${number(run.batch_size)} · ใช้เวลา ${(run.generation_micros / 1000).toFixed(1)} ms`}
            />
            <Stat
              label="ทุจริตที่ฝัง"
              value={number(run.fraud_injected)}
              unit="รายการ"
              foot={`${ratePercent(run.fraud_rate_bp)} ของ ${number(run.transaction_count)} · คงเหลือปกติ ${number(run.legit_count)}`}
            />
            <Stat
              label="ตรวจพบ"
              value={`${number(run.fraud_detected)}/${number(run.fraud_injected)}`}
              foot={`อัตราตรวจพบ ${ratePercent(run.detection_rate_bp)} · ความแม่นยำ ${ratePercent(run.precision_bp)}`}
            />
            <Stat
              label="p95 ต่อชุด"
              value={latency(run.p95_micros)}
              foot={`p50 ${latency(run.p50_micros)} · p99 ${latency(run.p99_micros)} · ${number(Math.round(run.throughput_per_sec))} รายการ/วินาที`}
            />
          </div>

          <div className="grid-main" style={{ marginTop: 16 }}>
            <div className="card card-pad">
              <div className="row" style={{ alignItems: 'baseline' }}>
                <span className="card-label">ผลรายกฎ · 13 รหัสของ ledger</span>
                <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                  พบ {number(firing.length)} จาก {number(results.length)} กฎ · {number(run.finding_count)} ครั้ง
                </span>
              </div>
              <div className="table-wrap" style={{ marginTop: 14 }}>
                <table className="table data">
                  <thead>
                    <tr>
                      <th>รหัสกฎ</th>
                      <th>ความรุนแรง</th>
                      <th style={{ textAlign: 'right' }}>ครั้งที่พบ</th>
                      <th style={{ textAlign: 'right' }}>รายการที่ถูกตั้งธง</th>
                      <th style={{ textAlign: 'right' }}>ตรวจพบรายการฝัง</th>
                      <th style={{ textAlign: 'right' }}>ยอดรวม</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r) => (
                      <tr key={r.rule_code} style={{ cursor: 'default', opacity: r.hit_count === 0 ? 0.55 : 1 }}>
                        <td>
                          <div className="mono" style={{ fontSize: 11.5 }}>{r.rule_code}</div>
                          <div className="dim" style={{ fontSize: 11.5 }}>{r.title_th}</div>
                        </td>
                        <td><Severity value={r.severity} /></td>
                        <td className="num tnum">{number(r.hit_count)}</td>
                        <td className="num tnum">{number(r.transaction_count)}</td>
                        <td className={`num tnum ${r.injected_transaction_count > 0 ? 'sev-high' : 'dim'}`}>
                          {number(r.injected_transaction_count)}
                        </td>
                        <td className="num tnum"><Money satang={r.amount_at_risk_satang} compact /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card card-pad">
              <div className="card-label">หลักฐานความสามารถทำซ้ำ</div>
              <dl style={{ margin: '14px 0 0', fontSize: 12, lineHeight: 2 }}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <dt className="dim">Seed</dt>
                  <dd className="mono" style={{ margin: 0 }}>{run.seed}</dd>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <dt className="dim">ช่วงข้อมูล</dt>
                  <dd style={{ margin: 0 }}>{thaiDateTime(run.window_start)} – {thaiDateTime(run.window_end)}</dd>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <dt className="dim">ยอดรวมฝั่งเดบิต</dt>
                  <dd style={{ margin: 0 }}>{money(run.debit_amount_satang, { compact: true })}</dd>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <dt className="dim">ยอดรวมฝั่งเครดิต</dt>
                  <dd style={{ margin: 0 }}>{money(run.credit_amount_satang, { compact: true })}</dd>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <dt className="dim">แจ้งเตือนผิดพลาด</dt>
                  <dd style={{ margin: 0 }}>{number(run.false_positive_count)} รายการ</dd>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <dt className="dim">เวลารวม</dt>
                  <dd style={{ margin: 0 }}>{(run.total_micros / 1000).toFixed(1)} ms</dd>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <dt className="dim">สร้างเมื่อ</dt>
                  <dd style={{ margin: 0 }}>{thaiDateTime(run.generated_at)}</dd>
                </div>
              </dl>
              <div style={{ marginTop: 14 }}>
                <div className="dim" style={{ fontSize: 11 }}>TOTALS HASH (SHA-256)</div>
                <div className="mono" title={run.totals_hash} style={{ fontSize: 11.5, wordBreak: 'break-all', marginTop: 6 }}>
                  {run.totals_hash}
                </div>
              </div>
              <p className="dim" style={{ fontSize: 11.5, lineHeight: 1.8, marginTop: 14, marginBottom: 0 }}>
                แฮชนี้ครอบทุกแถวที่สร้างและทุกผลรวม แต่ไม่ครอบเวลาที่วัดได้ การรันด้วย seed เดียวกันจึงต้องได้แฮชเดิม
                แม้ p95 จะต่างกันตามเครื่อง — ใช้ตรวจย้อนหลังได้ว่าเลขบนหน้านี้ไม่ถูกเขียนใหม่
              </p>
            </div>
          </div>

          <div className="card card-pad" style={{ marginTop: 16 }}>
            <div className="card-label">วิธีอ่านผล</div>
            <div className="grid-3" style={{ marginTop: 12 }}>
              <div>
                <div className="mono" style={{ fontSize: 11.5, color: 'var(--color-gold)' }}>ตรวจพบ / ฝัง</div>
                <p className="dim" style={{ fontSize: 12, lineHeight: 1.75, marginTop: 6, marginBottom: 0 }}>
                  ตัวเศษคือรายการที่ฝังไว้และถูกกฎอย่างน้อยหนึ่งข้อตั้งธง ตัวส่วนคือจำนวนที่ฝังจริงจากอัตราและ seed
                  ไม่ใช่จำนวนครั้งที่กฎยิง เพราะรายการเดียวอาจเข้าได้หลายกฎ
                </p>
              </div>
              <div>
                <div className="mono" style={{ fontSize: 11.5, color: 'var(--color-gold)' }}>ความแม่นยำ</div>
                <p className="dim" style={{ fontSize: 12, lineHeight: 1.75, marginTop: 6, marginBottom: 0 }}>
                  คำนวณจากรายการทั้งหมดที่ถูกตั้งธง การจ่ายเงินก้อนใหญ่ที่ถูกต้องย่อมถูกกฎขนาดตั้งธงได้
                  ตัวเลขนี้จึงแสดงต้นทุนของการใช้กฎขนาดตามลำพัง ไม่ได้แปลว่าโมเดลผิด
                </p>
              </div>
              <div>
                <div className="mono" style={{ fontSize: 11.5, color: 'var(--color-gold)' }}>p95 ต่อชุด</div>
                <p className="dim" style={{ fontSize: 12, lineHeight: 1.75, marginTop: 6, marginBottom: 0 }}>
                  เปอร์เซ็นไทล์ที่ 95 ของเวลาประมวลผลหนึ่งชุด 100 รายการ ใช้ nearest-rank เพราะหนึ่งชุดเป็นหน่วยงานที่แบ่งไม่ได้
                  ค่าที่ได้จึงเป็นเวลาที่เกิดขึ้นจริงของชุดใดชุดหนึ่ง ไม่ใช่ค่าประมาณระหว่างชุด
                </p>
              </div>
            </div>
            <p className="dim" style={{ fontSize: 11.5, lineHeight: 1.8, marginTop: 14, marginBottom: 0 }}>
              หมายเหตุ: ผลรวม {shortHash(run.totals_hash)} ตรวจสอบได้ด้วยการรัน seed {run.seed} จำนวน {number(run.transaction_count)} รายการ
              ที่อัตรา {ratePercent(run.fraud_rate_bp)} อีกครั้ง — ระบบไม่เคยอ่านหรือเขียนรายการจากตารางบัญชีจริง
            </p>
          </div>
        </>
      )}
    </>
  )
}
